import { defineStore } from 'pinia'

import * as handoverRequestApi from '../services/api/handover-request'
import { IApproval } from './staff'

export interface IHandoverRequestData {
  id: number
  status: string
  note: string
  reason: string
  approvals: Array<IApproval>
  received_at: string
  received_place: string
  warranty: boolean
  final_price: number
  loan_amount: number
  amount_accessory: number
  assignee_id: number
  car_id: number
  customer_id: number
  assignee: any
  car: any
  customer: any
  created_at: string
}

export const useHandoverRequestStore = defineStore('handoverRequest', {
  state: () => ({
    handoverRequest: {
      id: 0,
      status: '',
      note: '',
      reason: '',
      approvals: [],
      received_at: '',
      received_place: '',
      warranty: false,
      final_price: 0,
      loan_amount: 0,
      amount_accessory: 0,
      assignee_id: 0,
      car_id: 0,
      customer_id: 0,
      assignee: {},
      car: {},
      customer: {},
      created_at: '',
    } as IHandoverRequestData,
    isLoading: false,
  }),

  actions: {
    setHandoverRequest(data: IHandoverRequestData) {
      this.handoverRequest = data
    },

    setLoading(value: boolean) {
      this.isLoading = value
    },

    async getHandoverRequest(id: number) {
      try {
        this.setLoading(true)
        const { data } = await handoverRequestApi.get(id)
        this.setHandoverRequest(data)
        this.setLoading(false)
      } catch (e) {
        this.setLoading(false)
        throw new Error(String(e))
      }
    },

    async updateHandoverRequest(payload: any) {
      try {
        this.setLoading(true)
        const { data } = await handoverRequestApi.update(this.handoverRequest.id, payload)
        this.setHandoverRequest(data)
        this.setLoading(false)
      } catch (e) {
        this.setLoading(false)
        throw new Error(String(e))
      }
    },

    async applyHandoverRequest() {
      try {
        this.setLoading(true)
        const { data } = await handoverRequestApi.apply(this.handoverRequest.id)
        this.setHandoverRequest(data)
        this.setLoading(false)
      } catch (e) {
        this.setLoading(false)
        throw new Error(String(e))
      }
    },

    async rejectHandoverRequest(payload: any) {
      try {
        this.setLoading(true)
        const { data } = await handoverRequestApi.reject(this.handoverRequest.id, payload)
        this.setHandoverRequest(data)
        this.setLoading(false)
      } catch (e) {
        this.setLoading(false)
        throw new Error(String(e))
      }
    },

    async remakeHandoverRequest(payload: any) {
      try {
        this.setLoading(true)
        const { data } = await handoverRequestApi.remake(this.handoverRequest.id, payload)
        this.setHandoverRequest(data)
        this.setLoading(false)
      } catch (e) {
        this.setLoading(false)
        throw new Error(String(e))
      }
    },

    async approveHandoverRequest() {
      try {
        this.setLoading(true)
        const { data } = await handoverRequestApi.approve(this.handoverRequest.id)
        this.setHandoverRequest(data)
        this.setLoading(false)
      } catch (e) {
        this.setLoading(false)
        throw new Error(String(e))
      }
    },
  },
})

export const useHandoverRequestListStore = defineStore('handoverRequestList', {
  state: () => ({
    handoverRequests: [] as IHandoverRequestData[],
    isLoading: false,
    count: 0,
    total: 0,
  }),

  actions: {
    setCount(data: number) {
      this.count = data
    },

    addHandoverRequest(data: IHandoverRequestData) {
      this.handoverRequests.push(data)
    },

    setHandoverRequests(data: IHandoverRequestData[]) {
      this.handoverRequests = data
    },

    setLoading(value: boolean) {
      this.isLoading = value
    },

    setTotal(value: number) {
      this.total = value
    },

    async countHandoverRequests() {
      try {
        this.setLoading(true)
        const { data } = await handoverRequestApi.count()
        this.setCount(data)
        this.setLoading(false)
      } catch (e) {
        this.setLoading(false)
        throw new Error(String(e))
      }
    },

    async createHandoverRequest(payload: any) {
      try {
        this.setLoading(true)
        const { data } = await handoverRequestApi.create(payload)
        this.addHandoverRequest(data)
        this.setLoading(false)
        return data
      } catch (e) {
        this.setLoading(false)
        throw new Error(String(e))
      }
    },

    async listHandoverRequests(params: any) {
      try {
        this.setLoading(true)
        const result = await handoverRequestApi.list(params)
        const { data, total } = result as any
        this.setHandoverRequests(data)
        this.setTotal(total)
        this.setLoading(false)
        return result
      } catch (e) {
        this.setLoading(false)
        throw new Error(String(e))
      }
    },

    async listAllHandoverRequests(params: any) {
      try {
        this.setLoading(true)
        const result = await handoverRequestApi.listAll(params)
        const { data, total } = result as any
        this.setHandoverRequests(data)
        this.setTotal(total)
        this.setLoading(false)
        return result
      } catch (e) {
        this.setLoading(false)
        throw new Error(String(e))
      }
    },
  },
})
